import Vue from 'vue'
import { curryRight, debounce, identity, omitBy, random, sortBy, take } from 'lodash'
import EventBus from '../_event-bus'
import { $$, pipe, sleep } from '../_utils'

const omitByCurried = curryRight(omitBy)

const DEBOUNCE_THRESHOLD_MAX = 600
const DEBOUNCE_THRESHOLD_MIN = 200
const PAGE_SIZE = 8
const SORTING_FUNCTIONS = {
  default: prizes => sortBy(prizes),
  'points-asc': prizes => sortBy(prizes, 'points'),
  'points-desc': prizes => sortBy(prizes, 'points').reverse(),
}

Vue.component('vue-prizes-list', {
  template: '#vue-prizes-list-template',
  props: ['rawPrizes'],
  data() {
    return {
      filters: {},
      page: 1,
      sortOrder: 'default',
    }
  },
  computed: {
    sortedPrizes() {
      return this.sortAndFilterPrizes({
        filters: this.filters,
        sortOrder: this.sortOrder,
        prizes: this.rawPrizes,
      })
    },
    loadedPrizes() {
      return take(this.sortedPrizes, PAGE_SIZE * this.page)
    },
  },
  methods: {
    addLazyLoadListener,
    addSortAndFilterListeners,
    filterPrizes,
    initFilters,
    loadPrizes,
    setFilters,
    setSortOrder,
    sortAndFilterPrizes,
  },
  mounted() {
    this.addLazyLoadListener()
    this.addSortAndFilterListeners()
    this.initFilters()
  },
})

function addSortAndFilterListeners() {
  EventBus.$on('filter-prizes', this.setFilters)
  EventBus.$on('sort-prizes', this.setSortOrder)
}

function addLazyLoadListener() {
  window.addEventListener(
    'scroll',
    debounce(this.loadPrizes, random(DEBOUNCE_THRESHOLD_MIN, DEBOUNCE_THRESHOLD_MAX))
  )
}

function loadPrizes() {
  const isScrolledPastMarker =
    this.$refs.lazyLoadMarker.offsetTop - window.scrollY - window.innerHeight < 0
  if (!isScrolledPastMarker) return
  this.page++
}

function filterPrizes(filters) {
  if (!Object.keys(filters).length) return identity
  return prizes =>
    pipe(
      omitByAvailability(filters),
      omitByType(filters),
      omitByCategory(filters),
      omitByPoints(filters)
    )(prizes)
}

function initFilters() {
  EventBus.$emit('init-prize-filters', this.rawPrizes)
}

function omitByAvailability({ availability }) {
  return omitByCurried(({ isAvailable }) => availability && !isAvailable)
}

function omitByCategory({ afloCategories, eduCategories }) {
  return omitByCurried(
    ({ category, pointsType }) =>
      (pointsType === 'aflo' &&
        !!afloCategories.find(({ label, status }) => category === label && !status)) ||
      (pointsType === 'edu' &&
        !!eduCategories.find(({ label, status }) => category === label && !status))
  )
}

function omitByPoints({
  afloRange: [afloMin, afloMax],
  afloRangeMax: afloRangeMax,
  eduRange: [eduMin, eduMax],
  eduRangeMax: eduRangeMax,
}) {
  return omitByCurried(
    ({ points, pointsType }) =>
      (pointsType === 'aflo' &&
        (points < afloMin || (points > afloMax && afloMax < afloRangeMax))) ||
      (pointsType === 'edu' && (points < eduMin || (points > eduMax && eduMax < afloRangeMax)))
  )
}

function omitByType({ aflo, edu }) {
  return omitByCurried(
    ({ pointsType }) => (!aflo && pointsType === 'aflo') || (!edu && pointsType === 'edu')
  )
}

function sortAndFilterPrizes({ filters, prizes, sortOrder }) {
  return pipe(filterPrizes(filters), SORTING_FUNCTIONS[sortOrder])(prizes)
}

function setFilters(filters) {
  this.filters = filters
}

async function setSortOrder(order) {
  this.page = 0
  this.sortOrder = order
  await sleep(random(DEBOUNCE_THRESHOLD_MIN, DEBOUNCE_THRESHOLD_MAX))
  this.page = 1
}

const prizesLists = $$('#vue-prizes-list-wrapper')
window.addEventListener('DOMContentLoaded', () => {
  prizesLists.forEach(el => new Vue({ el }))
})
